import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserPlus, Search, Check, X, Bell, Activity, Loader2, Rocket, Trophy,
  Circle, UserCheck, Compass,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/Bits";
import RoomInvitesBanner from "@/components/RoomInvitesBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

function Avatar({ u, onClick }) {
  return (
    <button onClick={onClick} className="w-11 h-11 rounded-full grid place-items-center text-xl bg-gradient-to-br from-[#7c3aed] to-[#3b9eff] shrink-0 relative" data-testid={`avatar-${u.id}`}>
      {u.avatar_emoji || "🎮"}
      {u.online && <Circle className="w-3 h-3 absolute -bottom-0.5 -right-0.5 fill-emerald-500 text-emerald-500 bg-[hsl(var(--card))] rounded-full" />}
    </button>
  );
}

export default function Friends() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ requests: 0 });
  const goProfile = (id) => navigate(`/app/creators/${id}`);

  const loadCounts = useCallback(() => {
    api.get("/social/friends/requests").then((r) => setCounts({ requests: r.data.incoming.length })).catch(() => {});
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  return (
    <div className="p-5 lg:p-8 max-w-4xl mx-auto" data-testid="friends-page">
      <PageHeader title="Friends & Network" icon={Users} subtitle="Connect with creators and players, follow your favourites, and see what they're building." />
      <RoomInvitesBanner />
      <Tabs defaultValue="friends">
        <TabsList>
          <TabsTrigger value="friends" data-testid="friends-tab-friends"><Users className="w-4 h-4 mr-1.5" /> Friends</TabsTrigger>
          <TabsTrigger value="requests" data-testid="friends-tab-requests"><Bell className="w-4 h-4 mr-1.5" /> Requests{counts.requests > 0 && <Badge className="ml-1.5 h-5 px-1.5 bg-[hsl(var(--primary))]">{counts.requests}</Badge>}</TabsTrigger>
          <TabsTrigger value="discover" data-testid="friends-tab-discover"><Compass className="w-4 h-4 mr-1.5" /> Discover</TabsTrigger>
          <TabsTrigger value="activity" data-testid="friends-tab-activity"><Activity className="w-4 h-4 mr-1.5" /> Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="friends" className="mt-4"><FriendsList goProfile={goProfile} /></TabsContent>
        <TabsContent value="requests" className="mt-4"><Requests onChange={loadCounts} goProfile={goProfile} /></TabsContent>
        <TabsContent value="discover" className="mt-4"><Discover goProfile={goProfile} /></TabsContent>
        <TabsContent value="activity" className="mt-4"><Feed goProfile={goProfile} /></TabsContent>
      </Tabs>
    </div>
  );
}

function FriendsList({ goProfile }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/social/friends").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from friends?`)) return;
    try { await api.delete(`/social/friends/${id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <Spinner />;
  if (data.friends.length === 0) return <EmptyState emoji="🫂" title="No friends yet" text="Head to Discover to find creators and players, then send a friend request." />;
  return (
    <div data-testid="friends-list">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{data.friends.length} friends · {data.online_count} online</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {data.friends.map((u) => (
          <div key={u.id} className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3" data-testid={`friend-${u.id}`}>
            <Avatar u={u} onClick={() => goProfile(u.id)} />
            <div className="flex-1 min-w-0">
              <button onClick={() => goProfile(u.id)} className="font-bold truncate hover:underline">{u.name}</button>
              <div className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{u.online ? "Online" : "Offline"} · Lv {u.level}</div>
            </div>
            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(u.id, u.name)} data-testid={`remove-friend-${u.id}`}><X className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Requests({ onChange, goProfile }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/social/friends/requests").then((r) => { setData(r.data); onChange?.(); });
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/social/friends/requests/${id}/${action}`); load(); toast.success(action === "accept" ? "Friend added! 🎉" : "Declined"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <Spinner />;
  const { incoming, outgoing } = data;
  return (
    <div className="space-y-6" data-testid="friends-requests">
      <div>
        <h3 className="font-bold text-sm mb-2">Incoming ({incoming.length})</h3>
        {incoming.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="no-incoming">No incoming requests.</p> :
          <div className="space-y-2">
            {incoming.map((u) => (
              <div key={u.request_id} className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3" data-testid={`incoming-${u.request_id}`}>
                <Avatar u={u} onClick={() => goProfile(u.id)} />
                <div className="flex-1 min-w-0"><button onClick={() => goProfile(u.id)} className="font-bold truncate hover:underline">{u.name}</button><div className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{u.role} · Lv {u.level}</div></div>
                <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600" onClick={() => act(u.request_id, "accept")} data-testid={`accept-${u.request_id}`}><Check className="w-4 h-4" /> Accept</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(u.request_id, "decline")} data-testid={`decline-${u.request_id}`}><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>}
      </div>
      <div>
        <h3 className="font-bold text-sm mb-2">Sent ({outgoing.length})</h3>
        {outgoing.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No pending sent requests.</p> :
          <div className="space-y-2">
            {outgoing.map((u) => (
              <div key={u.request_id} className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 opacity-80">
                <Avatar u={u} onClick={() => goProfile(u.id)} />
                <div className="flex-1 min-w-0"><div className="font-bold truncate">{u.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">Pending…</div></div>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}

function UserRow({ u, goProfile, onAction }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3" data-testid={`user-${u.id}`}>
      <Avatar u={u} onClick={() => goProfile(u.id)} />
      <div className="flex-1 min-w-0">
        <button onClick={() => goProfile(u.id)} className="font-bold truncate hover:underline flex items-center gap-1">{u.name}{u.verified && <UserCheck className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />}</button>
        <div className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{u.role} · Lv {u.level}{u.followers !== undefined ? ` · ${u.followers} followers` : ""}</div>
      </div>
      {u.is_friend ? <Badge variant="secondary" className="text-[10px]">Friends</Badge> :
        u.request_pending ? <Badge variant="secondary" className="text-[10px]">Requested</Badge> :
          <Button size="sm" className="rounded-full" onClick={() => onAction(u.id)} data-testid={`add-friend-${u.id}`}><UserPlus className="w-4 h-4" /> Add</Button>}
    </div>
  );
}

function Discover({ goProfile }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [sugg, setSugg] = useState(null);

  useEffect(() => { api.get("/social/suggestions").then((r) => setSugg(r.data.suggestions)).catch(() => setSugg([])); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => { api.get("/social/users/search", { params: { q } }).then((r) => setResults(r.data.results)); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const addFriend = async (id) => {
    try { const { data } = await api.post("/social/friends/request", { to_user_id: id }); toast.success(data.status === "accepted" ? "Friend added! 🎉" : "Request sent!");
      setResults((rs) => rs?.map((u) => u.id === id ? { ...u, request_pending: data.status !== "accepted", is_friend: data.status === "accepted" } : u));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="friends-discover">
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players & creators by name…" className="pl-9" data-testid="user-search" />
      </div>
      {results ? (
        results.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="no-results">No one found. Try another name.</p> :
          <div className="space-y-2">{results.map((u) => <UserRow key={u.id} u={u} goProfile={goProfile} onAction={addFriend} />)}</div>
      ) : (
        <div>
          <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5"><Compass className="w-4 h-4 text-[hsl(var(--primary))]" /> Suggested creators</h3>
          {!sugg ? <Spinner /> : sugg.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No suggestions yet — search for people above.</p> :
            <div className="space-y-2" data-testid="friend-suggestions">{sugg.map((u) => <UserRow key={u.id} u={u} goProfile={goProfile} onAction={addFriend} />)}</div>}
        </div>
      )}
    </div>
  );
}

function Feed({ goProfile }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/social/feed").then((r) => setData(r.data.feed)); }, []);
  if (!data) return <Spinner />;
  if (data.length === 0) return <EmptyState emoji="📡" title="Your feed is quiet" text="Follow creators and add friends to see when they publish games and earn achievements." />;
  return (
    <div className="space-y-2" data-testid="activity-feed">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3" data-testid={`feed-item-${i}`}>
          <Avatar u={item.actor} onClick={() => goProfile(item.actor.id)} />
          {item.type === "published" ? (
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <Rocket className="w-4 h-4 text-emerald-500 shrink-0" />
              <div className="min-w-0"><span className="font-bold">{item.actor.name}</span> published <button onClick={() => navigate(`/app/arcade/${item.game.id}`)} className="font-semibold text-[hsl(var(--primary))] hover:underline">{item.game.title}</button></div>
            </div>
          ) : (
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500 shrink-0" />
              <div className="min-w-0"><span className="font-bold">{item.actor.name}</span> earned <span className="font-semibold">{item.achievement.icon} {item.achievement.title}</span></div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
